import './styles.scss'

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Col, Modal, Row } from 'antd'

import { PRODUCT_COMPARISON } from '@/libs/constants/productTypes'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'

import CustomButton from '../CustomButton'
import { CloseIcon } from '../Icons'
import ProductComparisonCard from './ProductComparisonCard'

interface ProductComparisonModalProps {
  showModal: boolean
  setShowModal: (value: boolean) => void
  productsData: Record<string, unknown>
}

function ProductComparisonModal({
  showModal = false,
  setShowModal,
  productsData,
}: ProductComparisonModalProps) {
  // productsData is current data pass to the modal
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [productList, setProductList] = useState(
    localStorage.getItem(PRODUCT_COMPARISON)
      ? JSON.parse(localStorage.getItem(PRODUCT_COMPARISON))
      : [],
  )
  const [productExtra, setProductExtra] = useState(null)

  const handleOk = () => {
    setShowModal(false)
  }

  const handleCancel = () => {
    setShowModal(false)
  }

  const handleToProductComparisonPage = () => {
    setShowModal(false)
    navigate(linkGenerator('/product-comparison'))
  }

  // Save Product Info to Local Storage
  const handleSaveProductComparison = useCallback((dataSource) => {
    if (localStorage.getItem(PRODUCT_COMPARISON)) {
      const localProductComparison = JSON.parse(
        localStorage.getItem(PRODUCT_COMPARISON),
      )
      if (localProductComparison?.length === 3) {
        // Already had 3 products on product comparison list case. If dataSrc is not in  product comparison list,
        // add it to product comparison extra
        if (
          !localProductComparison?.some(
            (product) => product.materialNumber === dataSource.materialNumber,
          )
        ) {
          // localStorage.setItem(
          //   PRODUCT_COMPARISON_EXTRA,
          //   JSON.stringify(dataSrc)
          // );
          setProductExtra(dataSource)
        }
      } else {
        // Product comparison has less then 3 products
        const newData = [...localProductComparison, dataSource]

        // Remove duplicate data by checking materialName
        const filteredData = Array.from(
          new Map(newData.map((e) => [e.materialNumber, e])).values(),
        )

        localStorage.setItem(PRODUCT_COMPARISON, JSON.stringify(filteredData))
        setProductList(filteredData)
        // Dispatch custom event to notify other components
        globalThis.dispatchEvent(new CustomEvent('productComparisonUpdated'))
      }
    } else {
      const newData = [dataSource]
      localStorage.setItem(PRODUCT_COMPARISON, JSON.stringify(newData))
      setProductList(newData)
      // Dispatch custom event to notify other components
      globalThis.dispatchEvent(new CustomEvent('productComparisonUpdated'))
    }
  }, [])

  // Remove one product comparison and add extra product comparison item to the list
  const removeProductComparisonItem = (materialNo) => {
    const newProductList = productList.filter(
      (product) => product.materialNumber !== materialNo,
    )
    newProductList.push(productExtra)

    localStorage.setItem(PRODUCT_COMPARISON, JSON.stringify(newProductList))
    // localStorage.removeItem(PRODUCT_COMPARISON_EXTRA);
    setProductList(newProductList)
    // Dispatch custom event to notify other components
    globalThis.dispatchEvent(new CustomEvent('productComparisonUpdated'))
    setProductExtra(null)
  }

  // Render Modal Title
  const renderModalTitle = () => {
    if (isValidArray(productList)) {
      const totalProducts = productList.length
      if (totalProducts === 1) {
        return t('productComparison.itemAdded')
      }
      if (totalProducts === 3) {
        // if (localStorage.getItem(PRODUCT_COMPARISON_EXTRA)) {
        if (productExtra) {
          return t('productComparison.fullProductTitle')
        }
      }
      return t('productComparison.selectedItemTotal', {
        total: totalProducts,
      })
    }
    return ''
  }

  // Render Product Card List
  const renderProductList = () => {
    if (isValidArray(productList)) {
      if (productExtra) {
        return (
          <ProductComparisonCard
            hasExtraProduct
            productList={productList}
            removeItemFunc={removeProductComparisonItem}
          />
        )
      }
      return <ProductComparisonCard productList={productList} />
    }
    return ''
  }

  // Render Summary
  const renderSummaryContent = () => {
    if (productList.length === 3) {
      if (productExtra) {
        // const productComparisonExtra = JSON.parse(
        //   localStorage.getItem(PRODUCT_COMPARISON_EXTRA)
        // );
        const arrayData = [productExtra]
        return (
          <div
            className="product-comparison-modal__summary"
            style={{ marginTop: 30 }}
          >
            <ProductComparisonCard productList={arrayData} />
            <Row>
              <Col lg={14} md={12} sm={24} xl={14} xs={24}>
                <p>{t('productComparison.fullProductTexts')}</p>
              </Col>
            </Row>
          </div>
        )
      }
      return (
        <div className="product-comparison-modal__summary">
          <Row>
            <Col lg={14} md={12} sm={24} xl={14} xs={24}>
              <h2>{t('productComparison.readyTitle')}</h2>
              <p>
                {t('productComparison.readyTexts', {
                  total: productList.length,
                })}
              </p>
            </Col>
          </Row>
        </div>
      )
    }
    return ''
  }

  // Modal Footer
  const renderFooterContent = () => {
    return (
      <Row>
        <Col lg={12} md={12} sm={24} xl={12} xs={24}>
          <CustomButton
            className="cart-bottom__action__link--with-icon"
            type="ghost"
            onClick={() => setShowModal(false)}
          >
            {t('cart.continueShopping')}
          </CustomButton>
        </Col>
        <Col lg={12} md={12} sm={24} xl={12} xs={24}>
          <CustomButton onClick={() => handleToProductComparisonPage()}>
            {t('productComparison.toProductComparison')}
          </CustomButton>
        </Col>
      </Row>
    )
  }

  // Init Modal
  useEffect(() => {
    if (showModal === true) {
      handleSaveProductComparison(productsData)
    } else {
      // clear extra slot when close the modal
      setProductExtra(null)
    }
  }, [showModal, productsData, handleSaveProductComparison])

  return (
    <Modal
      closeIcon={<CloseIcon />}
      footer={renderFooterContent()}
      open={showModal}
      title={renderModalTitle()}
      width={700}
      className={`product-comparison-modal${
        productList && productList.length > 1 ? ' multi' : ' single'
      }`}
      onCancel={handleCancel}
      onOk={handleOk}
    >
      {renderProductList()}
      {renderSummaryContent()}
    </Modal>
  )
}

export default ProductComparisonModal
